import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import ApiServices from '../../shared/ApiServices/ApiServices';
import { FloatingInput } from '../../shared/custom-ui';
import FormSectionSkeleton from '../../shared/custom-ui/skeleton/FormSectionSkeleton/FormSectionSkeleton';
import EditSaveCancelButtons from './EditSaveCancelButtons';
import { parse, isValid, format } from 'date-fns';
import useValidation from '../../hooks/formValidation/useValidation';

interface PatientProfile {
    [key: string]: string | undefined;
    PatientFirstName?: string;
    PatientMiddleName?: string;
    PatientLastName?: string;
    // PatientName is kept for internal/legacy reads only — never display it
    PatientName?: string;
    BirthDate?: string;
    GuardianName?: string;
    Sex?: string;
    NationalIdentificationNumber?: string;
    MRN?: string;
    PrimaryInsurance?: string;
    PrimaryAuthNumber?: string;
    PaymentStatus?: string;
    Address?: string;
    City?: string;
    State?: string;
    Country?: string;
    Contact?: string;
    Email?: string;
    EmergencyContactName?: string;
    EmergencyContactRelationship?: string;
    EmergencyContactPhone?: string;
}

export default function PatientDetails({ Patientid, initialData }: { Patientid?: string; initialData?: any }) {
    const [patient, setPatient] = useState<PatientProfile | null>(initialData ?? null);
    const [formData, setFormData] = useState<PatientProfile>(initialData ?? {});
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(!initialData);
    const [updating, setUpdating] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    const { validateField } = useValidation();

    const fieldMap = [
        { label: 'First Name', key: 'PatientFirstName' },
        { label: 'Middle Name', key: 'PatientMiddleName' },
        { label: 'Last Name', key: 'PatientLastName' },
        { label: 'Date of Birth', key: 'BirthDate', type: 'date' },
        { label: 'Guardian Name', key: 'GuardianName' },
        { label: 'Gender', key: 'Sex' },
        { label: 'National ID', key: 'NationalIdentificationNumber' },
        { label: 'MRN', key: 'MRN' },
        { label: 'Primary Insurance', key: 'PrimaryInsurance' },
        { label: 'Primary Auth Number', key: 'PrimaryAuthNumber' },
        { label: 'Payment Status', key: 'PaymentStatus', type: 'select' },
        { label: 'Address', key: 'Address' },
        { label: 'City', key: 'City' },
        { label: 'State', key: 'State' },
        { label: 'Country', key: 'Country' },
        { label: 'Contact', key: 'Contact' },
        { label: 'Email', key: 'Email' },
        { label: 'Emergency Contact Name', key: 'EmergencyContactName' },
        { label: 'Emergency Relationship', key: 'EmergencyContactRelationship' },
        { label: 'Emergency Phone', key: 'EmergencyContactPhone' },
    ];

    useEffect(() => {
        if (initialData) return;
        const fetchPatient = async () => {
            setLoading(true);
            try {
                const response = await ApiServices.getPatientProfile(Patientid, ['']);
                if (response?.patient) {
                    setPatient(response.patient);
                    setFormData(response.patient);
                }
            } finally {
                setLoading(false);
            }
        };
        if (Patientid) fetchPatient();
    }, [Patientid]);

    const handleInputChange = (key: string, value: string) => {
        setFormData((prev) => {
            const updated = {
                ...prev,
                [key]: value,
            };
            const changed = Object.keys(updated).some(
                (k) => updated[k] !== patient?.[k]
            );
            setHasChanges(changed);

            const newErrors: Record<string, string> = { ...errors };

            if (key === 'Email') {
                const emailError = validateField(value, [
                    { case: 'required' },
                    { case: 'email-format' }
                ]);
                if (emailError) newErrors.Email = emailError.msg;
                else delete newErrors.Email;
            }

            if (key === 'Contact') {
                const phoneError = validateField(value, [
                    { case: 'required' },
                    { case: 'phone-format' }
                ]);
                if (phoneError) newErrors.Contact = phoneError.msg;
                else delete newErrors.Contact;
            }

            if (key === 'EmergencyContactPhone') {
                const emergencyPhoneError = validateField(value, [
                    { case: 'required' },
                    { case: 'phone-format' }
                ]);
                if (emergencyPhoneError) newErrors.EmergencyContactPhone = emergencyPhoneError.msg;
                else delete newErrors.EmergencyContactPhone;
            }

            setErrors(newErrors);

            return updated;
        });
    };

    const handleSave = async () => {
        const newErrors: Record<string, string> = {};

        if (formData.Email) {
            const emailError = validateField(formData.Email, [
                { case: 'required' },
                { case: 'email-format' }
            ]);
            if (emailError) newErrors.Email = emailError.msg;
        }

        if (formData.Contact) {
            const phoneError = validateField(formData.Contact, [
                { case: 'required' },
                { case: 'phone-format' }
            ]);
            if (phoneError) newErrors.Contact = phoneError.msg;
        }

        if (formData.EmergencyContactPhone) {
            const emergencyPhoneError = validateField(formData.EmergencyContactPhone, [
                { case: 'required' },
                { case: 'phone-format' }
            ]);
            if (emergencyPhoneError) newErrors.EmergencyContactPhone = emergencyPhoneError.msg;
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            toast.error(Object.values(newErrors)[0]);
            return;
        }

        setErrors({});

        const updatedFields = Object.keys(formData).reduce((acc, key) => {
            const currentVal = formData[key];
            const originalVal = patient?.[key];

            if (currentVal === originalVal) return acc;

            if (key === 'BirthDate' && currentVal) {
                try {
                    const parsed = new Date(currentVal);
                    if (!isNaN(parsed.getTime())) {
                        acc[key] = format(parsed, 'dd/MM/yyyy');
                    }
                } catch {
                    acc[key] = currentVal;
                }
            } else {
                acc[key] = currentVal;
            }
            return acc;
        }, {} as Record<string, any>);

        if (Object.keys(updatedFields).length === 0) {
            setEditMode(false);
            return;
        }

        try {
            setUpdating(true);
            const transformedPayload = Object.entries(updatedFields).reduce((acc, [key, value]) => {
                const lowerKey = key.charAt(0).toLowerCase() + key.slice(1);
                acc[lowerKey] = value;
                return acc;
            }, {} as Record<string, any>);

            await ApiServices.modifyPatientProfile(Patientid, transformedPayload);
            toast.success('Patient details updated.');
            setPatient((prev) => ({ ...prev, ...updatedFields }));
            setEditMode(false);
        } catch (err: any) {
            let message = err?.response?.data?.message;
            if (typeof message === 'string') {
                message = message.replace(/^\[\s*'?(.*?)'?\s*\]$/, '$1');
            }
            toast.error(message);
        } finally {
            setUpdating(false);
        }
        setHasChanges(false);
    };

    const handleCancel = () => {
        const restoredData = { ...formData };
        Object.keys(formData).forEach((key) => {
            if (formData[key] !== patient?.[key]) {
                restoredData[key] = patient?.[key] || '';
            }
        });
        setFormData(restoredData);
        setEditMode(false);
        setErrors({});
        setHasChanges(false);
    };

    const getSafeDateValue = (value: string) => {
        if (!value) return '';
        try {
            const parsed = parse(value, 'dd/MM/yyyy', new Date());
            if (isValid(parsed)) return format(parsed, 'yyyy-MM-dd');
        } catch { }
        try {
            const parsed = new Date(value);
            if (!isNaN(parsed.getTime())) return parsed.toISOString().split('T')[0];
        } catch { }
        return '';
    };

    if (loading) {
        return <FormSectionSkeleton fields={20} title="Patient Details" />;
    }

    return (
        <section className="flex h-full flex-col rounded-[var(--radius-lg)] border" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-card)' }}>
            <div className="sticky top-0 z-10 border-b px-6 py-4 rounded-t-[var(--radius-lg)] flex items-center justify-between" style={{ background: 'var(--primary-25)', borderColor: 'var(--border-subtle)' }}>
                <h2 className="text-lg font-bold" style={{ color: 'var(--text-heading)' }}>Patient Details</h2>
            </div>

            <div
                className={`relative flex-1 pl-6 pr-4 py-4 ${updating ? 'overflow-hidden' : 'overflow-y-auto custom-scrollbar max-h-[calc(100vh-440px)]'}`}
            >
                <div className={`${updating ? 'pointer-events-none opacity-50' : ''}`}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                        {fieldMap.map(({ label, key, type }) =>
                            key === 'PaymentStatus' ? (
                                <div key={key}>
                                    <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>{label}</label>
                                    <select
                                        value={formData[key] || ''}
                                        onChange={e => handleInputChange(key, e.target.value)}
                                        disabled={!editMode}
                                        className="w-full rounded-[var(--radius-md)] border px-3 py-2 text-sm font-medium shadow-sm transition-all focus:ring-[3px] disabled:opacity-60"
                                        style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-subtle)', color: 'var(--text-primary)' }}
                                    >
                                        <option value="">Select Status</option>
                                        <option value="Paid">Paid</option>
                                        <option value="Unpaid">Unpaid</option>
                                        <option value="Pending">Pending</option>
                                    </select>
                                    {errors[key] && <p className="text-sm text-red-500 mt-1">{errors[key]}</p>}
                                </div>
                            ) : (
                                <FloatingInput
                                    key={key}
                                    label={label}
                                    name={key}
                                    value={type === 'date' ? getSafeDateValue(formData[key]) : formData[key] || ''}
                                    onChange={handleInputChange}
                                    readOnly={!editMode}
                                    type={type}
                                    error={errors[key]}
                                />
                            )
                        )}
                    </div>
                </div>

                {/* Updating overlay */}
                {updating && (
                    <div className="absolute inset-0 z-20 flex items-center justify-center rounded-[var(--radius-lg)]" style={{ background: 'var(--bg-card)', opacity: 0.7 }}>
                        <div className="h-8 w-8 animate-spin rounded-full border-4" style={{ borderColor: 'var(--primary-100)', borderTopColor: 'var(--primary-600)' }} />
                    </div>
                )}
            </div>

            <div className="sticky bottom-0 z-10 px-6 py-4 border-t rounded-b-[var(--radius-lg)]" style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-subtle)' }}>
                <EditSaveCancelButtons
                    editMode={editMode}
                    updating={updating}
                    loading={loading}
                    onEdit={() => setEditMode(true)}
                    onSave={handleSave}
                    onCancel={handleCancel}
                    hasChanges={hasChanges}
                />
            </div>
        </section>
    );
}
